// Session baseline tracker.
//
// The most compelling coaching output isn't absolute CdA (which depends on
// calibration accuracy and Cd assumptions) — it's the *delta* vs a stable
// reference. Both values come from the same camera and same physics model,
// so the delta is ±1% accurate even if absolute is ±8%.
//
// Flow:
//   1. Wait for stable calibration + position classification for N seconds.
//   2. Capture whatever CdA the rider is holding as their "baseline".
//   3. After that, report live CdA and the delta + watts saved/lost.
//   4. Also track best (lowest) CdA observed during the session.
//
// The baseline locks in once; it doesn't move with position changes.

export interface BaselineState {
  baselineCda: number | null;
  bestCda: number | null;
  framesStable: number;
  isLocked: boolean;
  capturedAt: number | null;
}

export class SessionBaseline {
  /** Number of consecutive stable frames needed before we lock the baseline. */
  private readonly stableFramesRequired: number;
  /** Max allowed CdA variation during stability window (as fraction). */
  private readonly stabilityThreshold: number;

  private baselineCda: number | null = null;
  private bestCda: number | null = null;
  private capturedAt: number | null = null;
  private framesStable = 0;
  private stabilityWindow: number[] = [];
  // Wave 20.49bu — Retired shoulderHipWindow / baselineShoulderHipPx.
  // The old depth-K correction that consumed them used a
  // pose-dependent quantity (pixel shoulder-hip distance)
  // and had the wrong sign under aero tuck. The real fix
  // lives in the pipeline: send a pose-invariant live
  // px_per_m to the server so it computes correct area_m² per
  // frame. This tracker no longer needs any depth reference.

  constructor({
    stableFramesRequired = 90, // ~3 seconds at 30 fps
    stabilityThreshold = 0.025, // within 2.5% of mean
  }: {
    stableFramesRequired?: number;
    stabilityThreshold?: number;
  } = {}) {
    this.stableFramesRequired = stableFramesRequired;
    this.stabilityThreshold = stabilityThreshold;
  }

  /**
   * Feed a smoothed CdA value each frame. Returns the current state
   * including delta vs baseline (if locked).
   *
   * @param cda smoothed CdA from the pipeline.
   * @param usable Track 1.2 sanity gate — when false (e.g. calibration health
   *   <0.4 or camera off-axis), the value is *still tracked for best-CdA*
   *   (you can still beat your record) but it neither contributes to the
   *   stability window nor counts toward baseline lock. This stops a 60°
   *   off-axis bad frame from locking a noisy baseline.
   */
  update(
    cda: number | null,
    usable: boolean = true,
  ): BaselineState & {
    delta: number | null;
    deltaPct: number | null;
  } {
    if (cda !== null && Number.isFinite(cda) && cda > 0) {
      // Track best — even unusable frames can beat a previous best because
      // best-CdA is observational and reported with a confidence label.
      if (usable && (this.bestCda === null || cda < this.bestCda)) {
        this.bestCda = cda;
      }

      if (this.baselineCda === null && usable) {
        // Still trying to lock baseline — use a sliding stability window.
        // Unusable frames are completely skipped here so a bad frame can't
        // poison the lock window.
        this.stabilityWindow.push(cda);
        if (this.stabilityWindow.length > this.stableFramesRequired) {
          this.stabilityWindow.shift();
        }

        if (this.stabilityWindow.length >= this.stableFramesRequired) {
          const mean =
            this.stabilityWindow.reduce((s, v) => s + v, 0) /
            this.stabilityWindow.length;
          const maxDev = Math.max(
            ...this.stabilityWindow.map((v) => Math.abs(v - mean) / mean)
          );
          if (maxDev < this.stabilityThreshold) {
            // Lock it
            this.baselineCda = mean;
            this.capturedAt = Date.now();
            this.framesStable = this.stabilityWindow.length;
          }
        }
      }
    }

    const delta =
      this.baselineCda !== null && cda !== null
        ? cda - this.baselineCda
        : null;
    const deltaPct =
      delta !== null && this.baselineCda !== null
        ? (delta / this.baselineCda) * 100
        : null;

    return {
      baselineCda: this.baselineCda,
      bestCda: this.bestCda,
      framesStable: this.framesStable,
      isLocked: this.baselineCda !== null,
      capturedAt: this.capturedAt,
      delta,
      deltaPct,
    };
  }

  /** How close we are to locking the baseline, 0..1. */
  lockProgress(): number {
    if (this.baselineCda !== null) return 1;
    return Math.min(
      1,
      this.stabilityWindow.length / this.stableFramesRequired
    );
  }

  reset() {
    this.baselineCda = null;
    this.bestCda = null;
    this.capturedAt = null;
    this.framesStable = 0;
    this.stabilityWindow = [];
  }
}
